"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { JsonLd } from "@/lib/jsonld";
import { regionFromPathname, SITE_URL, type Region } from "@/lib/region";
import { resolveRichTextHref } from "@/lib/storyblok-link";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { mergeSourced, type SourcedBlok } from "@/lib/sourced-blok";

type Asset = { filename?: string; alt?: string };

type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string };
};
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

type FlipCard = SbBlokData & {
  image?: Asset;
  title?: string;
  subtitle?: string;
  description?: string | RichTextDoc;
};

type GlobalFlipCardsFields = {
  title?: string;
  cards?: FlipCard[];
  bg_color?: string;
};

type GlobalFlipCardsBlok = SbBlokData & SourcedBlok<GlobalFlipCardsFields>;

const FIELDS: (keyof GlobalFlipCardsFields)[] = ["title", "cards", "bg_color"];

function richTextHasContent(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(richTextHasContent);
}

function richTextToText(node: RichTextNode | undefined): string {
  if (!node) return "";
  if (node.type === "text") return node.text ?? "";
  if (node.type === "hard_break") return "\n";
  const inner = (node.content ?? []).map(richTextToText).join("");
  return node.type === "paragraph" ? `${inner}\n` : inner;
}

function cardDescriptionText(value: string | RichTextDoc | undefined): string {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  return (value.content ?? []).map(richTextToText).join("").trim();
}

function physicianGraph(cards: FlipCard[]) {
  return cards
    .filter((card) => /^dr\.?\s/i.test((card.title ?? "").trim()))
    .map((card) => {
      const description = cardDescriptionText(card.description);
      return {
        "@type": "Physician",
        name: card.title,
        ...(card.subtitle ? { jobTitle: card.subtitle } : {}),
        ...(description ? { description } : {}),
        medicalSpecialty: "Reproductive Medicine",
        affiliation: {
          "@type": "MedicalOrganization",
          name: "Thérapie Fertility",
          url: SITE_URL,
        },
        worksFor: {
          "@type": "MedicalOrganization",
          name: "Thérapie Fertility",
        },
      };
    });
}

function renderCardNode(node: RichTextNode, key: number, region?: Region): ReactNode {
  switch (node.type) {
    case "paragraph":
      return <p key={key}>{node.content?.map((n, i) => renderCardNode(n, i, region))}</p>;
    case "hard_break":
      return <br key={key} />;
    case "text": {
      let el: ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className="underline underline-offset-2 hover:opacity-80"
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderCardNode(n, i, region)) ?? null;
  }
}

function CardDescription({ value, isOpen }: { value: string | RichTextDoc; isOpen: boolean }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const region = regionFromPathname(usePathname() ?? "/");

  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    if (!isOpen && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [isOpen]);

  let inner: ReactNode = null;
  if (typeof value === "string") {
    inner = <p className="whitespace-pre-line">{value}</p>;
  } else if (richTextHasContent(value)) {
    inner = (
      <div className="flex flex-col gap-3">
        {value.content?.map((n, i) => renderCardNode(n, i, region))}
      </div>
    );
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value]);

  if (!inner) return null;
  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        data-card-scroll
        tabIndex={overflows ? 0 : -1}
        className={`scrollbar-none text-body h-full touch-pan-y overscroll-contain leading-[1.56] [-ms-overflow-style:none] [scrollbar-width:none] focus:outline-none [&::-webkit-scrollbar]:hidden ${
          overflows ? "overflow-y-auto pb-14" : "overflow-hidden"
        }`}
      >
        {inner}
      </div>
      {overflows && (
        <div
          aria-hidden
          className="from-purple-05 pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-t to-transparent"
        />
      )}
    </div>
  );
}

export default function GlobalFlipCards({ blok: rawBlok }: { blok: GlobalFlipCardsBlok }) {
  const blok = mergeSourced(rawBlok, FIELDS);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const cards = blok.cards || [];
  const { bg } = resolveBg(blok.bg_color, "white");

  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const i = openIndex;
      setOpenIndex(null);
      requestAnimationFrame(() => triggerRefs.current[i]?.focus());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex]);

  const revealCard = (index: number) => {
    const scroller = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!scroller || !card) return;
    const sRect = scroller.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const cardLeft = cRect.left - sRect.left + scroller.scrollLeft;
    const target = cardLeft + cRect.width / 2 - scroller.clientWidth / 2;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: "smooth",
    });
  };

  const handleTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const total = triggerRefs.current.length;
    if (total === 0) return;
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % total;
    else if (e.key === "ArrowLeft") next = (index - 1 + total) % total;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = total - 1;
    else return;
    e.preventDefault();
    setActiveIndex(next);

    triggerRefs.current[next]?.focus({ preventScroll: true });
    revealCard(next);
  };

  const scrollByPage = (dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  const { ref: arrowsRef, rovingProps: arrowsRovingProps } = useRovingFocus<HTMLDivElement>();

  const arrowClass =
    "flex size-11 items-center justify-center rounded-full border border-black/10 text-purple-05 transition-colors hover:border-purple-03 hover:text-purple-03 aria-disabled:pointer-events-none aria-disabled:opacity-30";

  useEffect(() => {
    if (openIndex === null) return;
    const card = cardRefs.current[openIndex];
    const scroller = scrollerRef.current;
    if (!card || !scroller) return;

    const isLg = window.matchMedia("(min-width: 1024px)").matches;
    const expandedWidth = isLg ? 588 : window.innerWidth - 40;

    const cardRect = card.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const cardLeftInScroller = cardRect.left - scrollerRect.left + scroller.scrollLeft;
    const finalTarget = cardLeftInScroller + expandedWidth / 2 - scroller.clientWidth / 2;

    const startScroll = scroller.scrollLeft;
    const duration = 700;
    let startTs: number | null = null;
    let rafId = 0;

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const desired = startScroll + (finalTarget - startScroll) * easeOut(progress);
      const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      scroller.scrollLeft = Math.max(0, Math.min(desired, max));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [openIndex]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = cards.length - 1;
      setActiveIndex(closest);
      setAtStart(scroller.scrollLeft <= 1);
      setAtEnd(scroller.scrollLeft >= maxScroll - 1);
      const first = cardRefs.current[0];
      const last = cardRefs.current[cards.length - 1];
      if (first && last) {
        const span = last.offsetLeft + last.offsetWidth - first.offsetLeft;
        setIsScrollable(span > scroller.clientWidth - 16);
      } else {
        setIsScrollable(false);
      }
    };

    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [cards.length]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

      const inner = (e.target as HTMLElement)?.closest?.(
        "[data-card-scroll]",
      ) as HTMLElement | null;
      if (inner && inner.scrollHeight > inner.clientHeight) return;
      e.preventDefault();
      window.scrollBy({ top: e.deltaY });
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, []);

  if (cards.length === 0) return null;

  const physicians = physicianGraph(cards);

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      {physicians.length > 0 ? (
        <JsonLd data={{ "@context": "https://schema.org", "@graph": physicians }} />
      ) : null}
      <div className="pt-[30px] pb-[40px] lg:pt-[47px] lg:pb-[60px]">
        <div className="mx-auto flex w-full max-w-[1280px] items-center gap-6 px-[clamp(1.25rem,4vw,2.5rem)]">
          {blok.title && (
            <h2
              className={`text-purple-05 flex-1 text-center text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                isScrollable ? "lg:text-left" : ""
              }`}
            >
              {blok.title}
            </h2>
          )}
          {isScrollable && (
            <div
              ref={arrowsRef}
              {...arrowsRovingProps}
              className="hidden shrink-0 items-center gap-2 lg:flex"
            >
              <button
                type="button"
                data-roving
                onClick={() => scrollByPage(-1)}
                aria-disabled={atStart || undefined}
                aria-label="Previous cards"
                className={arrowClass}
              >
                <ChevronLeft className="size-5" strokeWidth={2} />
              </button>
              <button
                type="button"
                data-roving
                onClick={() => scrollByPage(1)}
                aria-disabled={atEnd || undefined}
                aria-label="Next cards"
                className={arrowClass}
              >
                <ChevronRight className="size-5" strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 lg:mx-auto lg:mt-10 lg:max-w-[1280px] lg:px-[clamp(1.25rem,4vw,2.5rem)]">
          <div
            ref={scrollerRef}
            tabIndex={-1}
            className={`scrollbar-none touch-pan-x touch-pan-y overflow-y-clip py-1 focus:outline-none [&::-webkit-scrollbar]:hidden ${
              isScrollable ? "overflow-x-auto" : "overflow-x-clip"
            }`}
          >
            <ul className="mx-auto flex w-max items-start gap-[18px] px-[clamp(1.25rem,4vw,2.5rem)] lg:gap-7 lg:px-0">
              {cards.map((card, i) => {
                const isOpen = openIndex === i;

                return (
                  <li
                    key={card._uid as string}
                    ref={(el) => {
                      cardRefs.current[i] = el;
                    }}
                    {...storyblokEditable(card)}
                    className={`relative shrink-0 transition-[width] duration-700 ease-out perspective-[1500px] ${
                      isOpen ? "w-[calc(100vw-2.5rem)] lg:w-[588px]" : "w-[286px] lg:w-[278px]"
                    }`}
                  >
                    <div
                      className={`relative h-[460px] transition-transform duration-700 ease-out transform-3d lg:h-[550px] ${
                        isOpen ? "transform-[rotateY(180deg)]" : ""
                      }`}
                    >
                      <button
                        ref={(el) => {
                          triggerRefs.current[i] = el;
                        }}
                        type="button"
                        onClick={() => setOpenIndex(i)}
                        onKeyDown={(e) => handleTriggerKey(e, i)}
                        aria-expanded={isOpen}
                        aria-hidden={isOpen}
                        tabIndex={!isOpen && i === 0 ? 0 : -1}
                        className="absolute inset-y-0 left-0 flex w-[286px] cursor-pointer flex-col overflow-hidden rounded-[18px] text-left backface-hidden lg:w-[278px] lg:rounded-[20px]"
                      >
                        <div className="bg-purple-01 relative aspect-286/280 w-full shrink-0 lg:aspect-286/340">
                          {card.image?.filename && (
                            <Image
                              src={card.image.filename}
                              alt={card.image.alt || ""}
                              fill
                              sizes="286px"
                              className="object-cover"
                            />
                          )}
                        </div>

                        <div className="bg-purple-00 flex flex-1 flex-col px-7 pt-7 pb-6">
                          {card.title && (
                            <p className="text-h5 leading-[1.08] font-bold text-black">
                              {card.title}
                            </p>
                          )}
                          {card.subtitle && (
                            <p className="text-body mt-2 leading-[1.64] text-[#595959]">
                              {card.subtitle}
                            </p>
                          )}
                          <span className="text-purple-03 text-body mt-auto pt-4 leading-[1.054] font-bold">
                            Learn more
                          </span>
                        </div>
                      </button>

                      <div
                        inert={!isOpen}
                        className="bg-purple-05 absolute inset-0 flex transform-[rotateY(180deg)] flex-col gap-4 rounded-[18px] px-7 pt-9 pb-9 text-white backface-hidden lg:rounded-[20px] lg:px-11 lg:pt-11 lg:pb-11"
                      >
                        {card.title && (
                          <p className="text-body leading-[1.12] font-bold">{card.title}</p>
                        )}
                        {card.description && (
                          <CardDescription value={card.description} isOpen={isOpen} />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setOpenIndex(null);
                            requestAnimationFrame(() => triggerRefs.current[i]?.focus());
                          }}
                          tabIndex={isOpen ? 0 : -1}
                          className="text-h6 text-purple-01 cursor-pointer self-start leading-[1.054] font-bold hover:underline"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {isScrollable && (
            <div className="mt-6 flex justify-center lg:hidden">
              <div className="flex items-center">
                {cards.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    tabIndex={-1}
                    onClick={() => {
                      cardRefs.current[i]?.scrollIntoView({
                        behavior: "smooth",
                        inline: "center",
                        block: "nearest",
                      });
                    }}
                    aria-label={`Go to card ${i + 1}`}
                    className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                  >
                    <span
                      aria-hidden
                      className={`block h-1.5 rounded-full transition-all duration-300 ${
                        activeIndex === i ? "bg-purple-03 w-8" : "bg-purple-01 w-2"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
